import { formatNumberWithSeparator } from 'utils/amount';

const formatCount = (value) => formatNumberWithSeparator(value);

const isValidLimitValue = (value) =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0;

export const DAILY_USAGE_WARNING_THRESHOLD = 85;

export const isValidCampaignLimitsData = (data) => {
    if (!data || typeof data !== 'object') return false;

    const { dailyLimit, monthlyLimit, dailyUsed, monthlyUsed } = data;

    return (
        isValidLimitValue(dailyLimit) &&
        isValidLimitValue(monthlyLimit) &&
        isValidLimitValue(dailyUsed) &&
        isValidLimitValue(monthlyUsed) &&
        dailyLimit > 0 &&
        monthlyLimit > 0
    );
};

const deriveCampaignLimitMetrics = ({ dailyLimit, monthlyLimit, dailyUsed, monthlyUsed }) => {
    const monthlyRemaining = Math.max(0, monthlyLimit - monthlyUsed);
    const dailyRemaining = Math.max(0, dailyLimit - dailyUsed);
    const isMonthlyLimitReached = monthlyUsed >= monthlyLimit;
    const availableToSend = Math.min(dailyRemaining, monthlyRemaining);
    const displayDailyLimit = isMonthlyLimitReached
        ? dailyUsed
        : Math.min(dailyLimit, dailyUsed + monthlyRemaining);

    return {
        monthlyRemaining,
        dailyRemaining,
        isMonthlyLimitReached,
        availableToSend,
        displayDailyLimit,
    };
};

export const getAvailableEmailsForSend = (limitsData) => {
    if (!isValidCampaignLimitsData(limitsData)) return null;

    return deriveCampaignLimitMetrics(limitsData).availableToSend;
};

export const getEmailsRemainingForSelection = (limitsData, selectedCount = 0) => {
    if (!isValidCampaignLimitsData(limitsData)) return null;

    const available = getAvailableEmailsForSend(limitsData);
    const selected = Number(selectedCount) || 0;

    return available - selected;
};

export const getCampaignSendLimitBlock = (limitsData, recipientsCount = 0) => {
    if (!isValidCampaignLimitsData(limitsData)) {
        return { blocked: false, tooltipKey: null };
    }

    const recipients = Number(recipientsCount) || 0;
    const { monthlyRemaining, isMonthlyLimitReached, availableToSend } =
        deriveCampaignLimitMetrics(limitsData);

    if (isMonthlyLimitReached) {
        return {
            blocked: true,
            tooltipKey: 'label_marketing_campaigns_monthly_limit_reached',
        };
    }

    if (availableToSend < recipients) {
        const tooltipKey =
            monthlyRemaining < recipients
                ? 'label_marketing_campaigns_monthly_limit_reached'
                : 'label_marketing_campaigns_daily_limit_reached';

        return { blocked: true, tooltipKey };
    }

    return { blocked: false, tooltipKey: null };
};

export const selectMarketingCampaignLimitsViewModel = (data) => {
    const { dailyLimit, monthlyLimit, dailyUsed, monthlyUsed } = data;
    const { displayDailyLimit, isMonthlyLimitReached } = deriveCampaignLimitMetrics(data);

    const dailyPercentage =
        displayDailyLimit === 0
            ? 100
            : Math.min(100, Math.round((dailyUsed / displayDailyLimit) * 100));

    const isDailyUsageHigh = dailyPercentage >= DAILY_USAGE_WARNING_THRESHOLD;
    const isDailyLimitReached = isMonthlyLimitReached || dailyUsed >= dailyLimit;

    // Progress bar cannot render 0/0 when monthly limit is exceeded with no daily usage.
    const dailyProgressValue = displayDailyLimit === 0 ? 1 : dailyUsed;
    const dailyProgressMax = displayDailyLimit === 0 ? 1 : displayDailyLimit;

    const blockingMessageKey = isMonthlyLimitReached
        ? 'label_marketing_campaigns_monthly_limit_reached'
        : isDailyLimitReached
          ? 'label_marketing_campaigns_daily_limit_reached'
          : null;

    return {
        dailyUsed,
        dailyLimit: displayDailyLimit,
        dailyProgressValue,
        dailyProgressMax,
        dailyPercentage,
        isDailyUsageHigh,
        isDailyLimitReached,
        isMonthlyLimitReached,
        blockingMessageKey,
        dailyUsageLiteralKey: 'label_marketing_campaigns_emails_used',
        dailyUsageLiteralParams: [formatCount(dailyUsed), formatCount(displayDailyLimit)],
        monthlyLimitLiteralKey: 'label_marketing_campaigns_monthly_limit',
        monthlyLimitLiteralParams: [formatCount(monthlyLimit)],
    };
};
